import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle, ArrowLeft } from 'lucide-react';
import { api } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';

type PaymentStatus = 'checking' | 'completed' | 'pending' | 'error';

function getApiBody(response: unknown): unknown {
    if (response && typeof response === 'object' && 'data' in (response as object)) {
        return (response as { data: unknown }).data;
    }
    return response;
}

export default function AgradecimentoVipPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { refreshTokenAndUpdateUser, isAuthenticated, isLoading: authLoading } = useAuth();
    const [checking, setChecking] = useState(true);
    const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>('checking');
    const [message, setMessage] = useState('');
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const refFromUrl = (searchParams.get('ref') ?? '').trim();

    const refreshUserData = useCallback(async () => {
        try {
            await refreshTokenAndUpdateUser();
        } catch (error) {
            console.error(error);
        }
    }, [refreshTokenAndUpdateUser]);

    const checkViaRef = useCallback(
        async (silent: boolean): Promise<boolean> => {
            if (!refFromUrl) return false;
            try {
                if (!silent) {
                    setChecking(true);
                    setPaymentStatus('checking');
                    setMessage('A confirmar pagamento com o servidor...');
                }
                const raw = await api.get('pagamento/verificar', {
                    params: { ref: refFromUrl },
                    requiresAuth: false,
                });
                const result = getApiBody(raw) as { status?: string };
                const st = result?.status;
                if (st === 'ativado' || st === 'ja_ativado') {
                    await refreshUserData();
                    if (!silent) {
                        setPaymentStatus('completed');
                        setMessage('VIP ATIVADO');
                    }
                    return true;
                }
                if (st === 'nao_pago') {
                    if (!silent) {
                        setPaymentStatus('pending');
                        setMessage('AGUARDANDO CONFIRMAÇÃO DO PAGAMENTO.');
                    }
                    return false;
                }
            } catch (e) {
                console.error(e);
                if (!silent) {
                    setPaymentStatus('error');
                    setMessage('ERRO AO CONFIRMAR O PAGAMENTO.');
                }
            } finally {
                if (!silent) setChecking(false);
            }
            return false;
        },
        [refFromUrl, refreshUserData],
    );

    const checkPendingPaymentsList = useCallback(
        async (silent: boolean) => {
            try {
                if (!silent) {
                    setChecking(true);
                    setPaymentStatus('checking');
                    setMessage('Verificando pagamentos...');
                }
                const raw = await api.get('meus-pagamentos', { requiresAuth: true });
                const body = getApiBody(raw) as { items?: unknown[] } | unknown[];
                const list = Array.isArray(body)
                    ? body
                    : Array.isArray((body as { items?: unknown[] })?.items)
                      ? (body as { items: unknown[] }).items
                      : [];
                if (!list.length) {
                    if (!silent) {
                        setPaymentStatus('error');
                        setMessage('Nenhum pagamento encontrado.');
                    }
                    return;
                }
                const pending = list.filter((p: any) => p?.status === 'pending');
                if (!pending.length) {
                    const completed = list.filter((p: any) => p?.status === 'completed');
                    if (completed.length > 0) {
                        if (!silent) {
                            setPaymentStatus('completed');
                            setMessage('VIP ATIVO');
                        }
                        await refreshUserData();
                    } else if (!silent) {
                        setPaymentStatus('error');
                        setMessage('Nenhum pagamento encontrado.');
                    }
                    return;
                }
                let completedCount = 0;
                for (const payment of pending) {
                    const id = (payment as { id?: number }).id;
                    if (typeof id !== 'number' || id <= 0) continue;
                    try {
                        const resRaw = await api.post(`meus-pagamentos/${id}/verificar`, {}, { requiresAuth: true });
                        const res = getApiBody(resRaw) as { status?: string };
                        if (res?.status === 'ativado' || res?.status === 'ja_ativado') completedCount += 1;
                    } catch (error) {
                        console.error(error);
                    }
                }
                await refreshUserData();
                if (!silent) {
                    if (completedCount > 0) {
                        setPaymentStatus('completed');
                        setMessage('PAGAMENTO CONFIRMADO. VIP ATIVADO.');
                    } else {
                        setPaymentStatus('pending');
                        setMessage('AGUARDANDO CONFIRMAÇÃO DO PAGAMENTO.');
                    }
                }
            } catch (error: unknown) {
                if (!silent) {
                    setPaymentStatus('error');
                    setMessage('ERRO AO VERIFICAR PAGAMENTOS.');
                }
            } finally {
                if (!silent) setChecking(false);
            }
        },
        [refreshUserData],
    );

    useEffect(() => {
        if (authLoading) return;
        if (!isAuthenticated) {
            const returnPath = `/agradecimento-vip${window.location.search}`;
            navigate(`/entrar?next=${encodeURIComponent(returnPath)}`, { replace: true });
            return;
        }

        const run = async () => {
            if (refFromUrl) {
                const ok = await checkViaRef(false);
                if (ok) return;
                intervalRef.current = setInterval(() => {
                    void checkViaRef(true);
                }, 60 * 1000);
                return;
            }

            await checkPendingPaymentsList(false);
            intervalRef.current = setInterval(() => {
                void checkPendingPaymentsList(true);
            }, 5 * 60 * 1000);
        };

        void run();

        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [authLoading, isAuthenticated, navigate, refFromUrl, checkViaRef, checkPendingPaymentsList]);

    if (authLoading) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center p-6">
                <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
            </div>
        );
    }

    return (
        <main className="min-h-screen bg-black flex items-center justify-center px-6 pt-28 pb-20">
            <div className="max-w-md w-full">
                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 text-white/40 hover:text-white mb-8 transition-all group"
                >
                    <ArrowLeft size={18} className="group-hover:-translate-x-1" />
                    <span className="text-sm font-bold">Voltar</span>
                </button>
                <div className="text-center space-y-6">
                    <div className="flex justify-center">
                        {paymentStatus === 'checking' || paymentStatus === 'pending' ? (
                            <Loader2
                                className={`w-16 h-16 ${paymentStatus === 'checking' ? 'text-orange-500' : 'text-white/20'} animate-spin`}
                            />
                        ) : paymentStatus === 'completed' ? (
                            <CheckCircle className="w-16 h-16 text-orange-500" />
                        ) : (
                            <XCircle className="w-16 h-16 text-white/20" />
                        )}
                    </div>
                    <div className="space-y-2">
                        <h1 className="text-3xl font-black text-white italic">
                            {paymentStatus === 'checking'
                                ? 'VERIFICANDO...'
                                : paymentStatus === 'completed'
                                  ? 'VIP ATIVADO'
                                  : paymentStatus === 'pending'
                                    ? 'AGUARDANDO'
                                    : 'ERRO'}
                        </h1>
                        <p className="text-white/60 font-medium uppercase tracking-wider text-sm">{message}</p>
                    </div>
                    {paymentStatus === 'completed' ? (
                        <div className="space-y-3 pt-4">
                            <Link
                                to="/meu-perfil"
                                className="block w-full py-4 bg-orange-600 text-white rounded-xl font-bold uppercase tracking-widest shadow-lg shadow-orange-900/20"
                            >
                                Ver Perfil
                            </Link>
                            <Link
                                to="/"
                                className="block w-full py-4 bg-neutral-900 text-white rounded-xl font-bold uppercase tracking-widest border border-white/5"
                            >
                                Ir para Home
                            </Link>
                        </div>
                    ) : (
                        <div className="space-y-3 pt-4">
                            <button
                                type="button"
                                onClick={() => (refFromUrl ? void checkViaRef(false) : void checkPendingPaymentsList(false))}
                                disabled={checking}
                                className="w-full py-4 bg-neutral-800 text-white rounded-xl font-bold uppercase tracking-widest disabled:opacity-50 border border-white/5"
                            >
                                {checking ? 'VERIFICANDO...' : 'VERIFICAR NOVAMENTE'}
                            </button>
                            <Link
                                to="/meu-perfil"
                                className="block w-full py-4 bg-neutral-900 text-white rounded-xl font-bold uppercase tracking-widest border border-white/5"
                            >
                                Ver Perfil
                            </Link>
                        </div>
                    )}
                    <p className="text-white/20 text-[10px] font-bold uppercase tracking-[0.2em]">
                        {paymentStatus === 'pending'
                            ? 'A confirmação pode levar alguns minutos.'
                            : 'Obrigado por apoiar a Mediocre Toons!'}
                    </p>
                </div>
            </div>
        </main>
    );
}
